import React, { useState } from 'react'
import styles from './Form.module.css'
import type { FormData } from './types'
import TextInput from './TextInput'

export default function Form() {
    const [formData, setFormData] = useState<FormData>({
        companyName: ``,
        contactName: '',
        phoneNumber: '',
        email: '',
        inquiryContent: '',
        consent: false,
    })

    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target
        const checked = (e.target as HTMLInputElement).checked

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            const csrfToken = document.querySelector<HTMLMetaElement>(
                'meta[name="csrf-token"]'
            )?.content

            const response = await fetch('/business/hotel/inquiry', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken || '',
                },
                body: JSON.stringify({
                    company_name: formData.companyName,
                    contact_name: formData.contactName,
                    phone_number: formData.phoneNumber,
                    email: formData.email,
                    inquiry_content: formData.inquiryContent,
                    consent: formData.consent,
                }),
            })

            const data = await response.json().catch(() => ({}))

            if (response.ok && data.success && data.redirect_url) {
                window.location.href = data.redirect_url
                return
            }

            if (response.ok) {
                setFormData({
                    companyName: '',
                    contactName: '',
                    phoneNumber: '',
                    email: '',
                    inquiryContent: '',
                    consent: false,
                })
            } else {
                alert(
                    data.errors?.join('\n') ||
                    'エラーが発生しました。もう一度お試しください。'
                )
            }
        } catch (error) {
            console.error('Error submitting form:', error)
            alert('エラーが発生しました。もう一度お試しください。')
        } finally {
            setIsSubmitting(false)
        }
    }

    const inputFields = [
        {
            title: '会社名',
            inputType: 'text',
            inputName: 'companyName',
            placeholder: '例）株式会社Hotelslabo',
            className: `${styles.textInput} ${styles.organization}`,
            value: formData.companyName,
            onChange: handleChange,
        },
        {
            title: '担当者名',
            inputType: 'text',
            inputName: 'contactName',
            placeholder: '山田太郎',
            className: `${styles.textInput} ${styles.name}`,
            value: formData.contactName,
            onChange: handleChange,
        },
        {
            title: '連絡先電話番号',
            inputType: 'tel',
            inputName: 'phoneNumber',
            placeholder: '例）03-1234-5678',
            className: `${styles.textInput} ${styles.tel}`,
            value: formData.phoneNumber,
            onChange: handleChange,
        },
        {
            title: 'メールアドレス',
            inputType: 'email',
            inputName: 'email',
            placeholder: '例）example@example.com',
            className: `${styles.textInput} ${styles.email}`,
            value: formData.email,
            onChange: handleChange,
        },
    ]

    return (
        <section id='form' className={styles.formSection}>
            <form
                className={styles.form}
                onSubmit={handleSubmit}
            >
                <h1>
                    <img
                        src='/pages/business/hotelslabo/form_h1.png'
                        alt='お問い合わせ'
                    />
                </h1>
                <dl className={styles.formList}>
                    {inputFields.map((inputField, index) => (
                        <TextInput
                            key={index}
                            title={inputField.title}
                            inputType={inputField.inputType}
                            inputName={inputField.inputName}
                            placeholder={inputField.placeholder}
                            className={inputField.className}
                            value={inputField.value}
                            onChange={inputField.onChange}
                        />
                    ))}
                    <dt className={styles.fieldLabel}>
                        <span className={styles.optionalLabel}>任意</span>
                        お問い合わせ内容
                    </dt>
                    <dd className={styles.fieldControl}>
                        <textarea
                            name='inquiryContent'
                            value={formData.inquiryContent}
                            onChange={handleChange}
                            className={styles.textarea}
                        />
                    </dd>
                    <p className={styles.privacy}>
                        <a
                            target='_blank'
                            rel='noopener noreferrer'
                            href='https://cuolega.co.jp/privacy/'
                        >
                            プライバシーポリシー
                        </a>
                        と
                        <a
                            target='_blank'
                            rel='noopener noreferrer'
                            href='https://cuolega.co.jp/terms/'
                        >
                            利用規約
                        </a>
                        をお読みくださいませ。
                    </p>
                    <p className={styles.consent}>
                        <label>
                            <input
                                className={styles.checkbox}
                                type='checkbox'
                                name='consent'
                                checked={formData.consent}
                                onChange={handleChange}
                                required
                            />
                            利用規約・プライバシーポリシーに同意する
                        </label>
                    </p>
                    <button
                        type='submit'
                        className={styles.button}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? '送信中...' : '問い合わせる'}
                    </button>
                </dl>
            </form>
        </section>
    )
}
